import './JQueryWrapper.css';

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useUnmountReactAndNavigate } from '@src/hooks';

/**
 * Navigate using react-router.
 * You can either provide a path or some navigation options.
 *
 * See: https://github.com/remix-run/react-router/blob/112ff0555d41acd99d15cd1f289d1c672dfba3d1/packages/react-router/lib/hooks.tsx#L142-L148
 *
 * @callback Navigate
 * @param {To} to The route to navigate to.
 * @param {NavigationOptions} [navigationOptions] The navigation options for more control.
 * @returns {void} Nothing
 */

/**
 * A navigation destination.
 * Can be a Path or an url. The url can be absolute ("/route") or relative ("route")
 *
 * See: https://github.com/remix-run/react-router/blob/main/packages/router/history.ts#L93-L98
 *
 * @typedef {Path | string} To
 */

/**
 * A path to go to.
 *
 * See: https://github.com/remix-run/react-router/blob/main/packages/router/history.ts#L32-L50
 *
 * @typedef Path
 * @property {string} [pathname] The route to navigate to. Can be absolute ("/route") or relative ("route").
 * @property {string} [search] The search query parameters. Must start with '?' and be correctly built.
 * @property {string} [hash] The hash part of the url.
 */

/**
 * The navigation options
 *
 * See: https://github.com/remix-run/react-router/blob/112ff0555d41acd99d15cd1f289d1c672dfba3d1/packages/react-router/lib/context.ts
 *
 * @typedef NavigationOptions
 * @property {boolean} [replace] True if you want the url to be replaced instead of adding an entry to the history.
 * @property {any} [state] Any state to pass to the navigation event.
 */

/**
 * A location entry in the history stack.
 *
 * See: https://github.com/remix-run/react-router/blob/112ff0555d41acd99d15cd1f289d1c672dfba3d1/packages/router/history.ts#L52-L69
 *
 * @typedef Location
 * @property {string} pathname The route to navigate to. Can be absolute ("/route") or relative ("route").
 * @property {string} search The search query parameters. Must start with '?' and be correctly built.
 * @property {string} hash The hash part of the url.
 * @property {any} state A state associated with this entry.
 * @property {string} key A unique key for this entry.
 */

/**
 * Executes the JQuery logic that needs to be run once the component is loaded.
 *
 * @callback ExecuteJQueryLogic
 * @param {Navigate} navigate
 * @returns {void | (() => void)} Cleanup logic for when we leave the page, if any.
 */

/**
 * A wrapper for JQuery managed pages.
 * This container replicates the original page structure that the JQuery code assumes exists.
 * All the jQuery code inject content into the #wrapper and #guiContainer elements.
 *
 * Ideally, we would get rid of these implicit assumptions and the JQuery code would be given the container.
 * But for now, just to get this rolling, we'll do this.
 *
 * We also pass the navigate callback to the jQuery code to allow navigation using react-router.
 *
 * @param {ExecuteJQueryLogic} executeJQueryLogic
 * @returns {JSX.Element}
 */
export const JQueryWrapper = ({ executeJQueryLogic }) => {
  /** @type {Navigate} */
  const navigate = useUnmountReactAndNavigate();

  useEffect(() => {
    // Why do we manage the #guiContainer inside useEffect and not like the #wrapper below?
    //
    // If we insert #guiContainer in the DOM like we do with #wrapper, React is going to manage the lifecycle of the div.
    // While this has numerous advantages, it prevents certain jQuery event listeners from firing, notably `.on("remove")`.
    // For this very important reason, we manually manage #guiContainer with jQuery.
    // Nothing uses #wrapper anymore, all the jQuery code implicitly use #guiContainer as their root container.
    const guiContainer = $('<div>')
      .attr('id', 'guiContainer')
      .addClass('display');

    $('#wrapper').append(guiContainer);

    const cleanup = executeJQueryLogic(navigate);

    return () => {
      cleanup?.();
      guiContainer.remove();
    };
  }, []);

  return (
    <div id='wrapper'></div>
  );
};

JQueryWrapper.propTypes = {
  executeJQueryLogic: PropTypes.func,
};
